import React, { useMemo } from 'react';
import { Box, Heading, Text, Loader, Divider } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Footer } from '../../../components/Footer';
import s from './MobileStoreName.scss';
import { shouldShowAcceptPayments } from '@common/utils';
import {
  SparklesSmall,
  CreditCardSmall,
  DesignSparklesSmall,
  MobileSmall,
  SiteContentSmall,
} from '@wix/wix-ui-icons-common';
import { isEmpty } from 'lodash';
import { AcceptTerms } from '../components/AcceptPayments/AcceptTerms';
import { useStoreNameData } from '../context/storeNameAtoms';
import { useStoreSuggestions } from '../hooks/useStoreSuggestions';
import { useStoreNameInput } from '../hooks/useStoreNameInput';
import { useStorePayments } from '../hooks/useStorePayments';
import { useStoreLaunch } from '../hooks/useStoreLaunch';
import { StoreNameInput } from '../components/StoreNameInput';
import { dataHooks } from '../datahooks';
import { ProgressBar } from '../../../components/ProgressBar/ProgressBar';
import { DynamicSelector } from '../../../components/DynamicSelector/DynamicSelector';
import { useColorThemeState } from '../../templateSelection/context';
import { PAYMENT_ACCEPTED_COUNTRIES } from '../../../consts';

const getLoaderSteps = (isShowPayments: boolean) => {
  const steps = [
    {
      label: 'CREATING_YOUR_SITE',
      icon: <SiteContentSmall />,
    },
    {
      label: 'OPTIMIZE_TO_MOBILE',
      icon: <MobileSmall />,
    },
    {
      label: 'APPLYING_THEME',
      icon: <DesignSparklesSmall />,
    },
  ];

  if (isShowPayments) {
    steps.push({
      label: 'CONNECTING_PAYMENTS',
      icon: <CreditCardSmall />,
    });
  }
  return steps;
};

export function MobileStoreName() {
  const { t } = useTranslation();
  const [{ isLoading: isThemeLoading }] = useColorThemeState();
  const isShowPayments = shouldShowAcceptPayments(PAYMENT_ACCEPTED_COUNTRIES);

  const storeNameData = useStoreNameData();
  const { getStoreNameSuggestions } = useStoreSuggestions();
  const { handleStoreNameChange, handleSuggestionSelect } = useStoreNameInput();
  const { handleAcceptPaymentsChange } = useStorePayments();
  const { launchStore, onComplete } = useStoreLaunch();

  const {
    isAcceptedPayments,
    selectedSuggestion,
    suggestions,
    launchStatus,
    isLoadingSuggestions,
    currentStoreName,
  } = storeNameData;

  const steps = useMemo(() => getLoaderSteps(isAcceptedPayments), [isAcceptedPayments]);

  const isLoading = isLoadingSuggestions && isEmpty(suggestions);

  if (launchStatus !== 'idle') {
    return (
      <Box align="center" height="100%" width="100%">
        <ProgressBar steps={steps} onComplete={onComplete} isLoading={launchStatus !== 'completed'} />
      </Box>
    );
  }

  const suggestionOptions = suggestions.map((suggestion) => ({
    value: suggestion,
    label: suggestion,
  }));

  return (
    <Box direction="vertical" height="100%" width="100%" gap={2} className={s.container}>
      <Box direction="vertical" className={s.contentWrapper} gap={4}>
        <Box direction="vertical" gap={1}>
          <Heading className={s.title} size="medium">
            {t('WHAT_YOUR_STORE_NAME')}
          </Heading>

          <Text>{t('PICK_MEMORABLE_NAME_TO_REPRESENT_YOUR_BRAND')}</Text>

          <Text className={s.textDisclaimer}>
            {t('WELL_CREATE_A_CONSISTENT_BRAND_BY_USING_YOUR_STORE_NAME_ACROSS_YOUR_SITE')}
          </Text>
        </Box>

        <StoreNameInput value={currentStoreName} onChange={handleStoreNameChange} />

        {isLoading ? (
          <Loader size="large" />
        ) : (
          <DynamicSelector
            options={suggestionOptions}
            onSelect={handleSuggestionSelect}
            selectedValue={selectedSuggestion}
            dataHook={dataHooks.StoreNameSuggestion}
            label={`${t('SUGGESTIONS')}:`}
            actionText={t('GENERATE_NEW_SUGGESTIONS')}
            actionIcon={<SparklesSmall />}
            onAction={getStoreNameSuggestions}
            isLoading={isLoadingSuggestions}
          />
        )}

        <Divider className={s.divider} />

        <AcceptTerms
          onCheckboxChange={handleAcceptPaymentsChange}
          isAccepted={isAcceptedPayments}
          isShowPayments={isShowPayments}
        />
      </Box>

      <Footer
        isDisabled={!currentStoreName}
        isLoading={isThemeLoading}
        onClick={launchStore}
        nextButtonText={t('LAUNCH_YOUR_STORE')}
        showSuffixIcon={false}
        className={s.templateFooter}
      />
    </Box>
  );
}
